/* eslint-disable no-param-reassign, default-case, prefer-template, react/react-in-jsx-scope */
import { HaltFetchDataError, noticeError } from 'es6/core/errors';
import * as API from 'es6/core/api';
import Config from 'es6/core/config';
import Keys from 'es6/core/keys';
import * as Loaders from 'es6/core/loaders';
import Logger from 'es6/services/logger';
import { schedule as scheduleMouseoverDictionaryLoad } from 'iknow-mouseover-dictionary/loader';
import { hideImmediately as hideMouseoverDictionary } from 'iknow-mouseover-dictionary/bubble';
import bindMouseoverDictionary from 'iknow-mouseover-dictionary';
import ServerTimeManager from 'es6/services/server-time-manager';
import * as StudyDataManager from 'es6/services/study-data-manager';
import TokenExpiredScreen from 'es6/components/apps/screens/token-expired';
import { pauseEvents, enableAutoPause, disableAutoPause } from 'es6/services/pause';
import { getPauseDelay } from 'es6/controllers/apps/debug-overrides';
import SessionProgressBar from 'es6/components/apps/session-progress-bar';
import ScreenBanner from 'es6/components/apps/screen-banner';

// Only need to load the static assets once per app. Keep a reference here.
let staticAssetLoader = null;

/**
 * Prevent browser to navigate using the back button.
 */
function disableDefaultBackspace() {
  window.addEventListener('keydown', preventDefaultBackspace, true);
  window.addEventListener('keypress', preventDefaultBackspace, true);
}

/**
 * Enable browser to navigate using the back button.
 */
function enableDefaultBackspace() {
  window.removeEventListener('keydown', preventDefaultBackspace, true);
  window.removeEventListener('keypress', preventDefaultBackspace, true);
}

/**
 * preventDefaultBackspace is used to prevent the browser to navigate
 * using the backspace key.
 *
 * @param {Event} e
 */
function preventDefaultBackspace(e) {
  if (e.keyCode === Keys.BACKSPACE) {
    e.preventDefault();
  }
}

/**
 * When extending this class, the _createSequencer() method needs to be overidden or an error will
 * be tossed.
 */
export default class BaseApp {
  constructor(applicationType) {
    this._setupElements();
    this._setupUtilities();
    this._setupHelpers();
    this._setupQuizTimer();
    this._setupScreenManager();
    this._setupScreens();
    this._setupStaticAssets();
    this._setupEvents();

    this._activeLoaders = [];
    this._applicationType = applicationType;
    this._isLoading = false;

    // Put it on the global namespace so legacy screens can read it.
    Smartfm.bindMouseoverDictionary = (root) => {
      let unbinds = [];

      root.find('.dictionary-target').each(
        (i, el) => unbinds.push(bindMouseoverDictionary(el))
      );

      return () => {
        unbinds.forEach(unbind => unbind());
        unbinds = [];
      };
    };
  }

  _setupElements() {
    this._elements = {
      homeButton:         document.querySelector('#home_button'),
      appContainer:       document.querySelector('.app-container'),
    };
  }

  _setupUtilities() {
    Smartfm.Apps.Utilities.VariableType();
    Smartfm.Apps.Utilities.DisallowKeys('PGUP', 'PGDOWN');

    this._completeItemDialogue = new Smartfm.Apps.Utilities.ModalWindow('master_item', { window: Smartfm.Apps.Common.Windows.SkipItem });
    this._skipItemDialogue = new Smartfm.Apps.Utilities.ModalWindow('skip_item', { window: Smartfm.Apps.Common.Windows.SkipItem });
  }

  _setupHelpers() {
    // TODO turn these into a module.
    this._trigger    = Smartfm.Apps.Helpers.Base.trigger;
    this._click      = Smartfm.Apps.Helpers.Base.addClickEvent;
  }

  _setupQuizTimer() {
    this._quizTimer = new Smartfm.Apps.QuizTimer();
    this._quizTimer.finished = () => {
      this._screenManager.fireScreenCallback('quizTimedOut');
    };

    // Timer should pause while text is being entered.
    $(document).bind('key_input_activity', () => {
      this._quizTimer.delay(2);
    });
  }

  _setupScreenManager() {
    this._screenManager = new Smartfm.Apps.ScreenManager();

    // TODO remove the need for this by making screenManager a module.
    Smartfm.screenManager = this._screenManager;
  }

  _setupScreens() {
    this._screenManager.addNavigation('app_navigation');
    this._screenManager.addPanel('bottom');
    this._screenManager.showPanels('bottom');

    this._screenManager.addScreen('loading', Smartfm.Apps.Common.Screens.Loading);
    this._screenManager.addScreen('error', Smartfm.Apps.iKnow.Screens.Error);

    // For skiping an item in session.
    this._screenManager.addEvent('skip_item_initiated', this._showSkipDialog.bind(this));
  }

  _setupEvents() {
    const handleHomeButton = this._handleHomeButton.bind(this);

    // Keep instances of the bound methods so they can be removed
    // in the unbindEvents() method.
    this._eventHandlers = {
      handleHomeButton,
    };

    this._elements.homeButton.addEventListener('click', handleHomeButton);

    // Disable ability in browser to go back.
    disableDefaultBackspace();

    // Hide any visible dictionary tooltips when the app is paused.
    pauseEvents.on('pause', hideMouseoverDictionary);

    pauseEvents.on('pause', () => this._pauseApplication());
    pauseEvents.on('resume', () => this._resumeApplication());
  }

  /**
   * @param {Event} e
   */
  _handleReloadPromptClose(e) {
    this._elements.reloadPrompt.hide();
  }

  /**
   * @param {Event} e
   */
  _handleReloadPromptReload(e) {
    window.location.reload();
  }

  /**
   * Takes a packed response and builds a session data object from it.
   *
   * @param {Object} packedData
   * @returns {Object}
   */
  _buildSessionData(packedData) {
    // Create item packages.
    packedData.itemPackages = _.chain(packedData.goals)
      .pluck('goalItems')
      .flatten()
      .indexBy(ip => ip.getId())
      .value();

    // Create sentences.
    packedData.sentences = _.chain(packedData.goals)
      .pluck('goalItems')
      .flatten()
      .pluck('sentences')
      .flatten()
      .indexBy(sentence => sentence.getId())
      .value();

    // This handles placing the memory on the appropriate sentence
    // or item package for single study session.
    if (Array.isArray(packedData.memories)) {
      packedData.memories.forEach(memory => {
        switch (memory.contentDomain) {
          case 'sentences':
            if (packedData.sentences[memory.contentId] !== 'undefined') {
              packedData.sentences[memory.contentId].memory = memory;
            }
            break;
          case 'items':
            if (packedData.itemPackages[memory.contentId] !== 'undefined') {
              packedData.itemPackages[memory.contentId].memory = memory;
            }
            break;
        }
      });
    }

    // Associate appropriate sentence or item package to study item.
    // Link the study item memory to the sentence or item package
    // if not specified.
    _.each(packedData.studySession, studyNode => {
      switch (studyNode.contentDomain) {
        case 'sentences':
          studyNode.content = packedData.sentences[studyNode.contentId];
          break;
        case 'items':
          studyNode.content = packedData.itemPackages[studyNode.contentId];
          break;
      }

      // Make sure the memories are the same.
      if (studyNode.content) {
        if (!studyNode.content.memory) {
          studyNode.content.memory = studyNode.memory;
        } else if (!studyNode.memory) {
          studyNode.memory = studyNode.content.memory;
        }
      }

      if (!studyNode.content) {
        Logger.error('Session Loader', 'No sentence or itemPackage for ' + studyNode.contentId + '.');
      }
    });

    // TODO: Set to error when loader logic is rewritten.
    // This should in theory be an error, but because it can occur and not
    // trigger an error, send it as a warning for now.
    if (packedData.studySession.length === 0) {
      Logger.warn('Session Loader', 'Study session has no items.');
    }

    return packedData;
  }

  /**
   * Aborts all active loaders.
   */
  _abortActiveLoaders() {
    this._activeLoaders.forEach(l => l.abort());
  }

  /**
   * Will add a loader to the active loader list. Removes the loader
   * from the active list when it finishes, is aborted or an error
   * occurs.
   *
   * @param {Loader} loader
   * @returns {Loader} loader
   */
  _addActiveLoader(loader) {
    this._activeLoaders.push(loader);

    // Callback to remove the loader when done.
    const removeLoader = () => {
      const index = this._activeLoaders.indexOf(loader);
      this._activeLoaders.splice(index, 1);
    };

    // When the loader finishes, remove it from the active loaders list.
    loader.then(removeLoader).catch(removeLoader);

    return loader;
  }

  /**
   * _preventDefaultBackspace is used to prevent the browser to navigate
   * using the backspace key.
   */
  _preventDefaultBackspace(e) {
    if (e.keyCode === Keys.BACKSPACE) {
      e.preventDefault();
    }
  }

  /**
   * Helper method by diagnostic, iknow and quick review to load
   * the Mouseover Dictionary after retrieving data from the loaders.
   * This can be chained with the response from the loaders.
   *
   * @param {Array} goals
   * @returns {Promise}
   */
  _setupMouseoverDictionary(goals) {
    const goalIds = goals.map((g) => g.id);
    const promise = scheduleMouseoverDictionaryLoad({ courses: goalIds });
    // We don't want to fail appliction load when dictionary fails.
    return promise.catch(error => error);
  }

  /**
   * Sets up static loaders.
   *
   * @returns {Promise}
   */
  _setupStaticAssets() {
    // If staticAssetLoader has not been defined, start loading the
    // assets for it.
    if (!staticAssetLoader) {
      staticAssetLoader = new Loaders.Assets({
        sounds: (new Smartfm.Apps.SoundSet(Smartfm.Apps.Sounds)).urls,
      });
    }

    return Promise.all([ServerTimeManager, staticAssetLoader]);
  }

  _checkUnsentSaveData(onSent) {
    StudyDataManager.checkForStoredPresentationData(
      () => {},
      onSent,
      error => this._errorHandler(error)
    );
  }

  _savePresentationResults(presentationResults, completeCallback) {
    if (Config.isLoggedIn()) {
      StudyDataManager.uploadPresentationData(presentationResults, this._applicationType)
        .then(completeCallback)
        .catch(error => this._errorHandler(error));
    } else {
      // Store locally until the user creates an account.
      StudyDataManager.storePresentationData(presentationResults, this._applicationType)
        .then(completeCallback)
        .catch(error => this._errorHandler(error));
    }
  }

  /**
   * Can be used to stop the chain of events in fetchData().
   */
  _haltFetchData() {
    throw new HaltFetchDataError();
  }

  /*
   * Error handler for the expected kinds of error that can occur
   * during Ajax requests.
   *
   * @param {Error} error
   */
  _errorHandler(error) {
    if (error.name === 'TokenExpiredError') {
      Logger.error('DataError', error);
      this._loading(false);
      this._screenManager.renderComponent(TokenExpiredScreen, { onReload: () => window.location.reload() });
    } else if (error.name === 'PaywallError') {
      this._screenManager.show('premium');
    } else if (error.name === 'AjaxAbortedError' || error.name === 'HaltFetchDataError') {
      // AjaxAbortedError only gets triggered when changing app settings and
      // Should not trigger anything in the app.
      return;
    } else {
      Logger.error('DataError', error, error.stack);

      // Send the error to new relic.
      noticeError(error);

      // Show a generic error.
      $(document).trigger('api_error', error);
    }
  }

  _pauseApplication() {
    if (!this._sessionInProgress()) {
      return;
    }
    this._quizTimer.pause();
    this._screenManager.pause();
    this._sequencer.pauseSequence();
  }

  _resumeApplication() {
    if (!this._sessionInProgress()) {
      return;
    }
    this._quizTimer.resume();
    this._screenManager.resume();
    this._sequencer.resumeSequence();
  }

  _sessionInProgress() {
    return this._sequencer && this._sequencer.inProgress();
  }

  /**
   * TODO: Fix this dialogue to just take an ID to skip.
   */
  _showSkipDialog() {
    this._skipItemDialogue.removeEvent('item_skipped');
    this._skipItemDialogue.addEvent('item_skipped', () => {
      // Remove the skipped item from the stored study session.
      // TODO a cleaner way of doing this
      const removeId = this._sequencer.activeQuiz.itemPackage.item.id;
      this._sessionData.studySession = _.filter(this._sessionData.studySession, studyNode => studyNode.contentId !== removeId);
      this._sequencer.skipCurrentItem();
    });
    this._skipItemDialogue.show();
  }

  /**
   * Callback for handling home button clicked.
   *
   * @param {Event} e
   */
  _handleHomeButton(e) {
    e.preventDefault();
    this._quitSession();
  }

  /**
   * Callback for handling quit button clicked.
   *
   * @param {Event} e
   */
  _handleQuitButton(e) {
    e.preventDefault();
    this._quitSession();
  }

  /**
   * Behavior for when the app is quit by the user. Does
   * not handle when the tab or window is closed.
   */
  _quitSession() {
    window.location = Config.getReferrerURL();
  }

  /**
   * Callback to trigger starting a new session.
   */
  _startSession() {
    if (this._isLoading) {
      return;
    }

    this._screenManager.show('loading');

    // Reset the banner.
    this._renderBanner();

    // Wait for assets to finish loading and start the sequencer.
    this._assetLoader.then(() => {
      // Create sequencer and start.
      this._sequencer = this._createSequencer();
      this._sequencer.startSequence(
        this._handleSequencerEnded.bind(this),
        this._handleSequencerProgress.bind(this));

      this._handleSequencerStarted();
    }).catch(this._errorHandler);
  }

  /**
   * Return a sequencer.
   *
   * @returns {SequencerBase}
   */
  _createSequencer() {
    throw new Error('_createSequencer() needs to be defined');
  }

  _handleSequencerStarted() {
    // Start the auto-pause service.
    enableAutoPause(getPauseDelay() || 90 * 1000);
  }

  _handleSequencerEnded() {
    // Stop the auto-pause service.
    disableAutoPause();
  }

  _handleSequencerProgress(progress) {
    this._renderBanner({
      showPauseButton: true,
      children: (<SessionProgressBar progress={progress} />),
    });
  }

  _renderBanner(props = {}) {
    const defaultProps = {
      showPauseButton: false,
      showSettingsButton: false,
      renderSettingsModalContents: (...args) => this._renderSettingsModalContents(...args),
      onQuit: (...args) => this._handleQuitButton(...args),
    };

    this._screenManager.renderBannerComponent(
      ScreenBanner,
      Object.assign(defaultProps, props)
    );
  }

  /**
   * Subclasses can override this to return a React component containing the contents of the
   * settings modal.
   */
  _renderSettingsModalContents(closeModal) {
    return null;
  }

  /**
   * Used to toggle the loading flag on the app.
   *
   * @param {Boolean} isLoadingToggle
   */
  _loading(isLoadingToggle) {
    const { appContainer } = this._elements;

    this._isLoading = isLoadingToggle;

    if (isLoadingToggle) {
      appContainer.classList.add('loading');
    } else {
      appContainer.classList.remove('loading');
    }
  }

  /**
   * Used by the summary panel and summary screen to skip individual items. Does
   * NOT retrieve a new session.
   */
  _skipItem(itemPackage, callback) {
    this._loading(true);

    API.postSkipItem(itemPackage.getId()).then(() => {
      itemPackage.memory.skipped = true;
      callback.call(this, itemPackage);
      this._loading(false);
    }).catch(function (reason) {
      Logger.error('Skip Item', reason, reason.stack);
      this._loading(false);
    });
  }

  _unbindEvents() {
    const { handleHomeButton } = this._eventHandlers;

    // Restore the default backspace button.
    enableDefaultBackspace();

    this._elements.homeButton.removeEventListener('click', handleHomeButton);

    pauseEvents.removeListener('pause', hideMouseoverDictionary);

    pauseEvents.removeListener('pause', () => this._pauseApplication());
    pauseEvents.removeListener('resume', () => this._resumeApplication());
  }

  /**
   * Used by the summary panel and summary screen to unskip individual items. Does
   * NOT retrieve a new session.
   */
  _unskipItem(itemPackage, callback) {
    this._loading(true);

    API.postUnskipItem(itemPackage.getId()).then(() => {
      itemPackage.memory.skipped = false;
      callback.call(this, itemPackage);
      this._loading(false);
    }).catch((reason) => {
      Logger.error('Unskip Item', reason, reason.stack);
      this._loading(false);
    });
  }

  /**
   * Used by summary panel and summary screen to set an item to mastered. Prompts
   * a modal (unreversible action warning).
   */
  _completeItemPrompt(itemPackage, callback) {
    this._completeItemDialogue.removeEvent('item_skipped');
    this._completeItemDialogue.addEvent('item_skipped', () => this._completeItem(itemPackage, callback));
    this._completeItemDialogue.show();
  }

  _completeItem(itemPackage, callback) {
    API.postMemoryCheckpoint([itemPackage.getId()]).then(() => {
      itemPackage.memory.setToComplete();
      callback(itemPackage);
      this._loading(false);
    }).catch(function (reason) {
      Logger.error('Master Item', reason, reason.stack);
      this._loading(false);
    });
  }
}
